import { supabase } from './supabase'
import { PushNotificationService } from './pushNotificationService'

export class NotificationService {
  // Event Notifications
  static async createEventNotification(
    eventId: string,
    eventTitle: string,
    type: 'event_created' | 'event_updated' | 'event_reminder',
    groupId: string,
    excludeUserId?: string
  ) {
    try {
      // Get all group members except the event creator
      const { data: members, error } = await supabase
        .from('group_members')
        .select('user_id')
        .eq('group_id', groupId)
        .neq('user_id', excludeUserId || '')

      if (error) throw error

      if (!members || members.length === 0) return

      const notifications = members.map(member => ({
        user_id: member.user_id,
        title: this.getNotificationTitle(type),
        message: this.getNotificationMessage(type, eventTitle),
        type,
        related_id: eventId,
        is_read: false
      }))

      const { error: insertError } = await supabase
        .from('notifications')
        .insert(notifications)

      if (insertError) throw insertError

      // Send push notifications to all members
      for (const member of members) {
        try {
          await PushNotificationService.sendPushNotification(
            member.user_id,
            this.getNotificationTitle(type),
            this.getNotificationMessage(type, eventTitle),
            { type: 'event', eventId, eventTitle }
          )
        } catch (pushError) {
          console.error('Error sending push notification for event:', pushError)
        }
      }
    } catch (error) {
      console.error('Error creating event notification:', error)
    }
  }

  static async createRSVPNotification(
    eventId: string,
    eventTitle: string,
    rsvpUserId: string,
    rsvpStatus: string,
    eventCreatorId: string
  ) {
    try {
      const notification = {
        user_id: eventCreatorId,
        title: 'New RSVP Received',
        message: `Someone RSVP'd "${rsvpStatus}" to your event "${eventTitle}"`,
        type: 'rsvp_received' as const,
        related_id: eventId,
        is_read: false
      }

      const { error } = await supabase
        .from('notifications')
        .insert([notification])

      if (error) throw error
    } catch (error) {
      console.error('Error creating RSVP notification:', error)
    }
  }

  // Task Notifications
  static async createTaskAssignedNotification(
    taskId: string,
    taskTitle: string,
    assignedUserId: string,
    assignedByUserId: string
  ) {
    try {
      // Debug logging removed

      // Don't notify if user assigned task to themselves
      if (assignedUserId === assignedByUserId) {
        return
      }

      // Create notification for the person who was assigned the task
      const assignedUserNotification = {
        user_id: assignedUserId,
        title: 'New Task Assigned',
        message: `You have been assigned a new task: "${taskTitle}"`,
        type: 'task_assigned' as const,
        related_id: taskId,
        is_read: false
      }

      const { error } = await supabase
        .from('notifications')
        .insert([assignedUserNotification])
        .select()

      if (error) {
        console.error('Error inserting task notification:', error)
        throw error
      }

      // Send push notification to the assigned user
      try {
        await PushNotificationService.sendPushNotification(
          assignedUserId,
          'New Task Assigned',
          `You have been assigned a new task: "${taskTitle}"`,
          { type: 'task_assigned', taskId, taskTitle }
        )
      } catch (pushError) {
        console.error('Error sending push notification:', pushError)
        // Don't throw error - push notification failure shouldn't break the main flow
      }

      // Create a simple confirmation notification for the assigner (no push notification)
      const assignerNotification = {
        user_id: assignedByUserId,
        title: 'Task Assignment Confirmation',
        message: `You have assigned the task "${taskTitle}" to a team member`,
        type: 'task_assigned' as const,
        related_id: taskId,
        is_read: false
      }

      const { error: assignerError } = await supabase
        .from('notifications')
        .insert([assignerNotification])
        .select()

      if (assignerError) {
        console.error('Error inserting assigner notification:', assignerError)
        // Don't throw error - assigner notification failure shouldn't break the main flow
      }
    } catch (error) {
      console.error('Error creating task assigned notification:', error)
    }
  }

  static async createTaskDueNotification(
    taskId: string,
    taskTitle: string,
    userId: string
  ) {
    try {
      const notification = {
        user_id: userId,
        title: 'Task Due Soon',
        message: `Your task "${taskTitle}" is due soon`,
        type: 'task_due' as const,
        related_id: taskId,
        is_read: false
      }

      const { error } = await supabase
        .from('notifications')
        .insert([notification])

      if (error) throw error
    } catch (error) {
      console.error('Error creating task due notification:', error)
    }
  }

  static async createTaskOverdueNotification(
    taskId: string,
    taskTitle: string,
    userId: string
  ) {
    try {
      const notification = {
        user_id: userId,
        title: 'Task Overdue',
        message: `Your task "${taskTitle}" is overdue`,
        type: 'task_overdue' as const,
        related_id: taskId,
        is_read: false
      }

      const { error } = await supabase
        .from('notifications')
        .insert([notification])

      if (error) throw error
    } catch (error) {
      console.error('Error creating task overdue notification:', error)
    }
  }

  static async createTaskCompletedNotification(
    taskId: string,
    taskTitle: string,
    completedByUserId: string,
    groupId: string
  ) {
    try {
      // Notify group admins when a task is completed
      const { data: admins, error } = await supabase
        .from('group_members')
        .select('user_id')
        .eq('group_id', groupId)
        .in('role', ['admin', 'manager'])
        .neq('user_id', completedByUserId)

      if (error) throw error

      if (!admins || admins.length === 0) return

      const notifications = admins.map(admin => ({
        user_id: admin.user_id,
        title: 'Task Completed',
        message: `A task "${taskTitle}" has been completed`,
        type: 'task_completed' as const,
        related_id: taskId,
        is_read: false
      }))

      const { error: insertError } = await supabase
        .from('notifications')
        .insert(notifications)

      if (insertError) throw insertError
    } catch (error) {
      console.error('Error creating task completed notification:', error)
    }
  }

  // Message Notifications
  static async createMessageNotification(
    messageId: string,
    senderId: string,
    conversationId: string,
    messageContent: string,
    isGroupConversation: boolean = false
  ) {
    try {
      let recipientIds: string[] = []

      if (isGroupConversation) {
        // Get all conversation participants except the sender
        const { data: participants, error } = await supabase
          .from('conversation_participants')
          .select('user_id')
          .eq('conversation_id', conversationId)
          .neq('user_id', senderId)

        if (error) throw error
        recipientIds = participants?.map(p => p.user_id) || []
      } else {
        // For direct messages, get the other participant
        const { data: participants, error } = await supabase
          .from('conversation_participants')
          .select('user_id')
          .eq('conversation_id', conversationId)
          .neq('user_id', senderId)

        if (error) throw error
        recipientIds = participants?.map(p => p.user_id) || []
      }

      if (recipientIds.length === 0) return

      const notifications = recipientIds.map(userId => ({
        user_id: userId,
        title: isGroupConversation ? 'New Group Message' : 'New Message',
        message: messageContent.length > 50 ? `${messageContent.substring(0, 50)}...` : messageContent,
        type: 'message_received' as const,
        related_id: messageId,
        is_read: false
      }))

      const { error: insertError } = await supabase
        .from('notifications')
        .insert(notifications)

      if (insertError) throw insertError

      // Send push notifications to all recipients
      for (const userId of recipientIds) {
        try {
          await PushNotificationService.sendPushNotification(
            userId,
            isGroupConversation ? 'New Group Message' : 'New Message',
            messageContent.length > 50 ? `${messageContent.substring(0, 50)}...` : messageContent,
            { type: 'message', messageId, conversationId, senderId }
          )
        } catch (pushError) {
          console.error('Error sending push notification for message:', pushError)
        }
      }
    } catch (error) {
      console.error('Error creating message notification:', error)
    }
  }

  // Group Notifications
  static async createGroupInvitationNotification(
    groupId: string,
    groupName: string,
    invitedUserId: string,
    invitedByUserId: string
  ) {
    try {
      const notification = {
        user_id: invitedUserId,
        title: 'Group Invitation',
        message: `You have been invited to join "${groupName}"`,
        type: 'group_invitation' as const,
        related_id: groupId,
        is_read: false
      }

      const { error } = await supabase
        .from('notifications')
        .insert([notification])

      if (error) throw error
    } catch (error) {
      console.error('Error creating group invitation notification:', error)
    }
  }

  static async createGroupMemberJoinedNotification(
    groupId: string,
    groupName: string,
    newMemberUserId: string,
    newMemberName: string
  ) {
    try {
      // Notify group admins when someone joins
      const { data: admins, error } = await supabase
        .from('group_members')
        .select('user_id')
        .eq('group_id', groupId)
        .in('role', ['admin', 'manager'])
        .neq('user_id', newMemberUserId)

      if (error) throw error

      if (!admins || admins.length === 0) return

      const notifications = admins.map(admin => ({
        user_id: admin.user_id,
        title: 'New Member Joined',
        message: `${newMemberName} joined "${groupName}"`,
        type: 'group_member_joined' as const,
        related_id: groupId,
        is_read: false
      }))

      const { error: insertError } = await supabase
        .from('notifications')
        .insert(notifications)

      if (insertError) throw insertError
    } catch (error) {
      console.error('Error creating group member joined notification:', error)
    }
  }

  // Curriculum Notifications
  static async createCurriculumSharedNotification(
    curriculumId: string,
    curriculumTitle: string,
    sharedWithGroupId: string,
    sharedByUserId: string
  ) {
    try {
      // Notify group members when curriculum is shared with their group
      const { data: members, error } = await supabase
        .from('group_members')
        .select('user_id')
        .eq('group_id', sharedWithGroupId)
        .neq('user_id', sharedByUserId)

      if (error) throw error

      if (!members || members.length === 0) return

      const notifications = members.map(member => ({
        user_id: member.user_id,
        title: 'Curriculum Shared',
        message: `A curriculum "${curriculumTitle}" has been shared with your group`,
        type: 'curriculum_shared' as const,
        related_id: curriculumId,
        is_read: false
      }))

      const { error: insertError } = await supabase
        .from('notifications')
        .insert(notifications)

      if (insertError) throw insertError
    } catch (error) {
      console.error('Error creating curriculum shared notification:', error)
    }
  }

  // Utility methods
  static async markAsRead(notificationId: string) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId)

      if (error) throw error
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  static async markAllAsRead(userId: string) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false)

      if (error) throw error
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }

  static async getUnreadCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false)

      if (error) throw error
      return count || 0
    } catch (error) {
      console.error('Error getting unread count:', error)
      return 0
    }
  }

  static async deleteNotification(notificationId: string) {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)

      if (error) throw error
    } catch (error) {
      console.error('Error deleting notification:', error)
    }
  }

  // Helper methods for notification content
  private static getNotificationTitle(type: string): string {
    switch (type) {
      case 'event_created':
        return 'New Event Created'
      case 'event_updated':
        return 'Event Updated'
      case 'event_reminder':
        return 'Event Reminder'
      case 'rsvp_received':
        return 'New RSVP Received'
      case 'task_assigned':
        return 'New Task Assigned'
      case 'task_due':
        return 'Task Due Soon'
      case 'task_overdue':
        return 'Task Overdue'
      case 'task_completed':
        return 'Task Completed'
      case 'message_received':
        return 'New Message'
      case 'group_invitation':
        return 'Group Invitation'
      case 'group_member_joined':
        return 'New Member Joined'
      case 'curriculum_shared':
        return 'Curriculum Shared'
      default:
        return 'Notification'
    }
  }

  private static getNotificationMessage(type: string, entityTitle: string): string {
    switch (type) {
      case 'event_created':
        return `A new event "${entityTitle}" has been created in your group.`
      case 'event_updated':
        return `The event "${entityTitle}" has been updated.`
      case 'event_reminder':
        return `Reminder: "${entityTitle}" is happening soon!`
      case 'rsvp_received':
        return `Someone RSVP'd to your event "${entityTitle}".`
      default:
        return 'You have a new notification.'
    }
  }
} 